<template>
    <div class="flex flex-col">
        <UiComponentTypography v-if="haveDiscount" :variant="priceVariant" :class-name="priceClass">
            <span>
                {{ formattedPrice }}
            </span>
        </UiComponentTypography>

        <UiComponentTypography :variant="discountedVariant" :class-name="showOptions.priceClass">
            <span>
                {{ formattedDiscountedPrice }}
            </span>
        </UiComponentTypography>
    </div>
</template>

<script lang="ts" setup>
type TypographyVariant =
    | "h1"
    | "h2"
    | "h3"
    | "h4"
    | "h5"
    | "h6"
    | "body"
    | "body2"
    | "caption"
    | "subtitle"
    | "subtitle2";

type PriceSize = "DEFAULT" | "sm" | "md" | "lg" | "xl" | "2xl";

interface IOptions {
    priceVariant?: TypographyVariant;
    discountedVariant?: TypographyVariant;
    priceClass?: string;
    discountClass?: string;
}

interface IVariants {
    priceVariant: TypographyVariant;
    discountedVariant: TypographyVariant;
}

const props = defineProps({
    price: {
        type: Number,
        required: true,
    },
    discount: {
        type: Number,
        default: 0,
    },
    options: {
        type: Object as PropType<IOptions>,
        default: () => ({
            priceClass: "text-amazon-500",
            discountClass: "opacity-75 line-through",
        }),
    },
    size: {
        type: String as PropType<PriceSize>,
        default: "DEFAULT",
    },
});

const sizeToVariantMap: Record<PriceSize, IVariants> = {
    sm: { priceVariant: "subtitle2", discountedVariant: "subtitle" },
    md: { priceVariant: "body2", discountedVariant: "h6" },
    lg: { priceVariant: "h6", discountedVariant: "h4" },
    xl: { priceVariant: "h6", discountedVariant: "h3" },
    "2xl": { priceVariant: "h5", discountedVariant: "h1" },
    DEFAULT: { priceVariant: "h6", discountedVariant: "h5" },
};

const haveDiscount = computed(() => Boolean(props.discount && props.discount > 0));

const showOptions = computed<IOptions>(() => props.options);

const sizeVariant = sizeToVariantMap[props.size] ?? sizeToVariantMap["DEFAULT"];

const priceVariant = computed(() => sizeVariant.priceVariant);

const discountedVariant = computed(() => sizeVariant.discountedVariant);

const priceClass = computed(() =>
    haveDiscount.value ? showOptions.value.discountClass : showOptions.value.priceClass
);

const formatCurrency = (value: number) => commaNumber(value ?? 0, { currencySymbol: "تومان" });

const formattedPrice = computed(() => formatCurrency(props.price));
const formattedDiscountedPrice = computed(() =>
    haveDiscount.value ? formatCurrency(props.discount) : formattedPrice.value
);
</script>

<style scoped></style>
